<template>
  <b-card>
    <div class="d-flex justify-content-between align-items-center">
      <h5>Display Settings</h5>
      <b-button variant="success" :disabled="saving" @click="handleSave">
        <b-spinner v-if="saving" small class="mr-50" />
        <feather-icon v-else icon="SaveIcon" />
        Save Changes</b-button
      >
    </div>
    <div v-if="loading" class="text-center py-3">
      <b-spinner variant="primary" />
    </div>
    <b-form v-else>
      <b-row>
        <b-col cols="12" md="6">
          <b-form-group>
            <label class="font-weight-bold" for="site-name">Site Name</label>
            <b-form-input id="site-name" v-model="form.siteName"></b-form-input>
          </b-form-group>
          <b-form-group>
            <label class="font-weight-bold" for="site-description"
              >Site Description</label
            >
            <b-form-textarea
              id="site-description"
              v-model="form.siteDescription"
              no-resize
              rows="2"
            ></b-form-textarea>
          </b-form-group>
          <b-form-group>
            <label class="font-weight-bold" for="timezone">Timezone</label>
            <v-select
              :value="form.timezone"
              :options="timezones"
              :clearable="false"
              :reduce="(val) => val.key"
              @input="(val) => (form.timezone = val)"
            />
          </b-form-group>
          <b-form-group>
            <label class="font-weight-bold" for="date-format"
              >Date Format</label
            >
            <v-select
              :value="form.dateFormat"
              :options="dateFormats"
              :clearable="false"
              :reduce="(val) => val.key"
              @input="(val) => (form.dateFormat = val)"
            />
          </b-form-group>
          <b-form-group>
            <label class="font-weight-bold" for="currency">Currency</label>
            <v-select
              :value="form.currency"
              :options="currencies"
              :clearable="false"
              :reduce="(val) => val.key"
              @input="(val) => (form.currency = val)"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <p class="font-weight-bold mb-1">(System Admin use only)</p>
          <b-form-group>
            <label class="font-weight-bold" for="time-format"
              >Time Format</label
            >
            <v-select
              :value="form.timeFormat"
              :options="timeFormats"
              :clearable="false"
              :reduce="(val) => val.key"
              @input="(val) => (form.timeFormat = val)"
            />
          </b-form-group>
          <b-form-group>
            <label class="font-weight-bold" for="language"
              >Default Language</label
            >
            <v-select
              :value="form.defaultLanguage"
              :options="languages"
              :clearable="false"
              :reduce="(val) => val.key"
              @input="(val) => (form.defaultLanguage = val)"
            />
          </b-form-group>
          <b-form-group>
            <label class="font-weight-bold" for="log-level">System Level Logging</label>
            <v-select
              :value="form.systemLogLevel"
              :options="logLevels"
              :clearable="false"
              :reduce="(val) => val.key"
              @input="(val) => (form.systemLogLevel = val)"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-form>
  </b-card>
</template>

<script>
import {
  BCard,
  BForm,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BButton,
  BFormTextarea,
  BSpinner,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapState, mapActions } from "vuex";
import useToastHandler from "@/services/toastHandler";

export default {
  components: {
    BCard,
    BForm,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BButton,
    BFormTextarea,
    BSpinner,
    vSelect,
  },
  data() {
    const timeFormats = [
      { key: "12h", label: "12 Hour (AM/PM)" },
      { key: "24h", label: "24 Hour" },
    ];
    const languages = [
      { key: "en", label: "English" },
      { key: "en-AU", label: "English (Australia)" },
      { key: "fr", label: "French" },
      { key: "de", label: "German" },
    ];
    const timezones = [
      {
        key: "Australia/Melbourne",
        label: "Australia/Melbourne (AEST)",
      },
      { key: "Australia/Sydney", label: "Australia/Sydney (AEST)" },
      { key: "Australia/Perth", label: "Australia/Perth (AWST)" },
      { key: "UTC", label: "UTC" },
    ];
    const dateFormats = [
      { key: "YYYY-MM-DD", label: "YYYY-MM-DD" },
      { key: "DD/MM/YYYY", label: "DD/MM/YYYY" },
      { key: "MM-DD-YYYY", label: "MM-DD-YYYY" },
    ];
    const currencies = [
      { key: "AUD", label: "AUD - Australian Dollar" },
      { key: "USD", label: "USD - US Dollar" },
      { key: "EUR", label: "EUR - Euro" },
      { key: "GBP", label: "GBP - British Pound" },
    ];
    const logLevels = [
      { key: "error", label: "Error Only" },
      { key: "warn", label: "Warning & Above" },
      { key: "info", label: "Info & Above" },
      { key: "debug", label: "Debug (All)" },
    ];
    return {
      form: {
        siteName: "",
        siteDescription: "",
        timezone: timezones[0].key,
        dateFormat: dateFormats[0].key,
        currency: currencies[0].key,
        timeFormat: timeFormats[0].key,
        defaultLanguage: languages[0].key,
        systemLogLevel: logLevels[0].key,
      },
      timeFormats,
      languages,
      timezones,
      dateFormats,
      currencies,
      logLevels,
    };
  },
  computed: {
    ...mapState("settings/display-settings", ["loading", "saving"]),
  },
  created() {
    this.fetchSettings();
  },
  methods: {
    ...mapActions("settings/display-settings", [
      "getDisplaySettings",
      "updateDisplaySettings",
    ]),
    async fetchSettings() {
      const data = await this.getDisplaySettings();
      if (data?.value) {
        const v = data.value;
        this.form = {
          siteName: v.siteName || "",
          siteDescription: v.siteDescription || "",
          timezone: v.timezone || this.timezones[0].key,
          dateFormat: v.dateFormat || this.dateFormats[0].key,
          currency: v.currency || this.currencies[0].key,
          timeFormat: v.timeFormat || this.timeFormats[0].key,
          defaultLanguage: v.defaultLanguage || this.languages[0].key,
          systemLogLevel: v.systemLogLevel || this.logLevels[0].key,
        };
      }
    },
    async handleSave() {
      const result = await this.updateDisplaySettings(this.form);
      if (result) {
        this.showToastSuccess("Settings saved", "Display settings updated successfully.");
      }
    },
  },
  setup() {
    const { showToastSuccess } = useToastHandler();
    return { showToastSuccess };
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
